import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import { useCursorStore } from '../../store/cursorStore';

/** Shared sync key so every chart's cursor tracks together on the x (distance) axis. */
export const SYNC_KEY = 'tla-workspace';

interface UplotChartProps {
  data: uPlot.AlignedData;
  series: uPlot.Series[];
  height: number;
  /** Axis scale opts keyed by scale name (e.g. y ranges). */
  yRange?: [number | null, number | null];
  title?: string;
  /** Fixed x window [min,max] in metres, or null to auto-fit. */
  xRange?: [number, number] | null;
  /** Called when the user drag-selects a range (or double-clicks to reset). */
  onZoom?: (range: [number, number] | null) => void;
}

/**
 * Thin React wrapper around uPlot. Charts share a cursor via {@link SYNC_KEY};
 * hovering publishes the distance to the cursor store and a plugin renders the
 * locked (click) line. Resizes to its container width.
 */
export function UplotChart(props: UplotChartProps): React.ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  // Keep the latest onZoom accessible from stable uPlot hooks.
  const onZoomRef = useRef(props.onZoom);
  onZoomRef.current = props.onZoom;
  // Re-applies the resting cursor (Distance = 0 or the locked position) whenever
  // the pointer is away, so the legend always shows values and stays a constant
  // height instead of collapsing to dashes.
  const pinRestRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const setActive = useCursorStore.getState().setActive;
    const toggleLock = useCursorStore.getState().toggleLock;
    // Set while a drag-zoom is completing so the trailing click doesn't lock.
    let justZoomed = false;

    const lockLine: uPlot.Plugin = {
      hooks: {
        draw: (u) => {
          const locked = useCursorStore.getState().locked;
          if (locked == null) return;
          const cx = u.valToPos(locked, 'x', true);
          const ctx = u.ctx;
          ctx.save();
          ctx.strokeStyle = '#ffd24a';
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 3]);
          ctx.beginPath();
          ctx.moveTo(cx, u.bbox.top);
          ctx.lineTo(cx, u.bbox.top + u.bbox.height);
          ctx.stroke();
          ctx.restore();
        },
      },
    };

    const opts: uPlot.Options = {
      width: host.clientWidth || 600,
      height: props.height,
      title: props.title,
      cursor: {
        sync: { key: SYNC_KEY, setSeries: true },
        drag: { x: true, y: false, setScale: false },
        points: { size: 5 },
      },
      legend: { live: true },
      scales: {
        x: { time: false },
        y: props.yRange
          ? { range: () => [props.yRange![0] ?? null, props.yRange![1] ?? null] as uPlot.Range.MinMax }
          : {},
      },
      axes: [
        {
          stroke: '#8b97a6',
          grid: { stroke: '#232a33' },
          label: 'Distance (m)',
          values: (_u, splits) => splits.map((v) => Math.round(v).toString()),
        },
        { stroke: '#8b97a6', grid: { stroke: '#232a33' } },
      ],
      series: props.series.map((s, i) =>
        i === 0
          ? { ...s, value: (_u: uPlot, v: number) => (v == null ? '—' : Math.round(v).toString()) }
          : s,
      ),
      plugins: [lockLine],
      hooks: {
        setCursor: [
          (u) => {
            const { left } = u.cursor;
            if (left == null || left < 0) {
              setActive(null);
              return;
            }
            setActive(u.posToVal(left, 'x'));
          },
        ],
        // Drag-select on the x-axis publishes a zoom range to the workspace,
        // which propagates to every chart. We clear the native selection so
        // uPlot doesn't also zoom this single chart locally.
        setSelect: [
          (u) => {
            const sel = u.select;
            if (sel.width <= 2) return;
            const min = u.posToVal(sel.left, 'x');
            const max = u.posToVal(sel.left + sel.width, 'x');
            u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
            justZoomed = true;
            if (max > min) onZoomRef.current?.([min, max]);
          },
        ],
        ready: [
          (u) => {
            u.over.addEventListener('click', () => {
              if (justZoomed) {
                justZoomed = false;
                return;
              }
              const left = u.cursor.left;
              if (left != null && left >= 0) toggleLock(u.posToVal(left, 'x'));
            });
            // Double-click clears the zoom back to the full lap.
            u.over.addEventListener('dblclick', () => onZoomRef.current?.(null));
          },
        ],
      },
    };

    const plot = new uPlot(opts, props.data, host);
    plotRef.current = plot;

    // Pin the cursor to the live/playback position when one is active, else to
    // the locked distance or Distance = 0, so the legend keeps showing values
    // and the cursor tracks the reference car during realtime playback (which
    // updates the store programmatically with no mouse event to drive it).
    const pinRest = (): void => {
      const p = plotRef.current;
      if (!p) return;
      const { active, locked } = useCursorStore.getState();
      const min = p.scales.x.min;
      const max = p.scales.x.max;
      if (min == null || max == null) return;
      const dist = active != null ? active : locked != null ? locked : min;
      const frac = max > min ? (dist - min) / (max - min) : 0;
      const left = Math.min(Math.max(frac, 0), 1) * p.over.clientWidth;
      p.setCursor({ left, top: 1 }, false);
    };
    pinRestRef.current = pinRest;
    const unsubCursor = useCursorStore.subscribe(pinRest);
    pinRest();

    const ro = new ResizeObserver(() => {
      plot.setSize({ width: host.clientWidth, height: props.height });
      pinRest();
    });
    ro.observe(host);

    return () => {
      unsubCursor();
      pinRestRef.current = null;
      ro.disconnect();
      plot.destroy();
      plotRef.current = null;
    };
    // Recreate the plot when the series identity or height changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.series, props.height]);

  // Update data without recreating the plot.
  useEffect(() => {
    plotRef.current?.setData(props.data);
    pinRestRef.current?.();
  }, [props.data]);

  // Update x zoom window. A null range restores the full data extent.
  useEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;
    if (props.xRange) {
      plot.setScale('x', { min: props.xRange[0], max: props.xRange[1] });
    } else {
      const xs = props.data[0] as number[] | undefined;
      if (xs && xs.length > 0) plot.setScale('x', { min: xs[0], max: xs[xs.length - 1] });
    }
    pinRestRef.current?.();
  }, [props.xRange, props.data]);

  return <div className="graph-block" ref={hostRef} />;
}
